import {
  Box,
  FormControl,
  useBreakpointValue,
  Button,
  Icon,
  useColorMode,
} from "@chakra-ui/react";

import FullCalendar from "@fullcalendar/react";
// The import order DOES MATTER here. If you change it, you'll get an error!
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import daygridPlugin from "@fullcalendar/daygrid";
// The import order DOES MATTER ^
import {
  Color,
  ColorName,
  FSize,
} from "theme/old-design-system/styled-components";
import AgentDomain from "entities/domain/agents/agent-domain";
import { ReactComponent as PlusIcon } from "assets/icons/Plus-ds.svg";
import CalendarBookingDomain from "entities/domain/calendar/calendar-domain";
import useAgentsStore from "hooks/use-agents-store";
import useCalendarStore from "hooks/use-calendar-store";
import useContactsStore from "hooks/use-contacts-store";
import { cleanContactsToastMessagesAction } from "redux/actions/contacts";
import { useDispatch } from "react-redux";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import Select, {
  ActionMeta,
  GroupBase,
  MultiValue,
  StylesConfig,
} from "react-select";
import { UpdateCalendarEventPayload } from "redux/actions/calendar";
import { toast } from "react-toastify";
import { transformFromAgentDomainToOptionType } from "entities/transformers/agent-transformer";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import { EventApi, EventChangeArg } from "@fullcalendar/core";
import { getReactSelectStyles } from "util/methods";
import EditEventModal from "./EditEventModal";
import NewEventModal from "./NewEventModal";
import ColourStyles from "./AgentDropDownStyles";
import NotifyGuestModal from "./NotifyGuestModal";
import EventInfoPopover from "./EventInfoPopover";

interface FullCalendarProps {
  agents: AgentDomain[];
}

export interface AgentOptionTypes {
  value: number;
  label: string;
  color: string;
}

interface EventDetailsEType {
  event: EventApi;
}

interface ExtendedProps {
  booking: CalendarBookingDomain;
  agentId: number | null;
}
interface BookingEvent {
  title: string;
  start: string;
  end: string;
  extendedProps: ExtendedProps;
  backgroundColor: string;
}

const FullCalendarComponent = ({ agents }: FullCalendarProps) => {
  const {
    events,
    fetchEvents,
    updateEvent,
    errors,
    selectedAgentIds,
    setSelectedAgentIds,
  } = useCalendarStore();
  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();
  const { getAgentById, currentAgent, getAgentsByIds } = useAgentsStore();
  const { toastMessage: contactToastMessage, filterContactById } =
    useContactsStore();
  const dispatch = useDispatch();

  const agentOptions: AgentOptionTypes[] = agents.map(
    transformFromAgentDomainToOptionType
  );

  const [currentExtendedProps, setCurrentExtendedProps] =
    useState<ExtendedProps>();

  const [openEventForm, setOpenEventForm] = useState<boolean>(false);
  const [openEditForm, setOpenEditForm] = useState<boolean>(false);
  const [openEventInfo, setOpenEventInfo] = useState<boolean>(false);
  const [showNotifyModal, setShowNotifyModal] = useState<boolean>(false);
  const [newEventStartDate, setNewEventStartDate] = useState<Date | null>(
    new Date()
  );
  const [newEventEndDate, setNewEventEndDate] = useState<Date | null>(
    new Date()
  );
  const [changedEventInfo, setChangedEventInfo] = useState<EventChangeArg>();
  const [highlightColor, setHighlightColor] = useState<ColorName>(
    currentAgent.color
  );

  useEffect(() => {
    setSelectedAgentIds([currentAgent.id]);
  }, []);

  const handleSelectedAgents = (e: MultiValue<AgentOptionTypes>) => {
    setSelectedAgentIds(e.map((ag) => ag.value));
  };

  const isBookingEventSelectedByAgent = (booking: BookingEvent): boolean => {
    if (selectedAgentIds.length === 0) {
      return true;
    }

    return (
      selectedAgentIds.filter(
        (a: number) => a === booking.extendedProps.agentId
      ).length !== 0
    );
  };

  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const transformToEventsPerAgentId = (
    booking: CalendarBookingDomain,
    agentId: number | null
  ): BookingEvent => {
    const customer = filterContactById(booking.customerId);

    return {
      title: `${booking.formatTitle()}${
        customer ? `, ${customer.getDisplayName()}` : ""
      }`,
      start: booking.startAt,
      end: booking.endAt,
      extendedProps: {
        booking,
        agentId,
      },
      backgroundColor: agentId
        ? getAgentById(agentId).color.value
        : `var(--chakra-colors-${colorScheme}-400)`,
    };
  };

  const transformToEvents = (
    booking: CalendarBookingDomain
  ): BookingEvent[] => {
    if (booking.agentIds.length === 0) {
      return [transformToEventsPerAgentId(booking, null)];
    }

    return booking.agentIds.map((aId) =>
      transformToEventsPerAgentId(booking, aId)
    );
  };

  const showOpenNewEventForm = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    setNewEventStartDate(startDate);
    setNewEventEndDate(endDate);
    setOpenEventForm(true);
  };

  const showEventDetails = (e: EventDetailsEType) => {
    if (
      currentExtendedProps?.agentId === e.event.extendedProps.agentId &&
      currentExtendedProps?.booking?.id === e.event.extendedProps.booking.id
    ) {
      setCurrentExtendedProps(undefined);
      setOpenEventInfo(false);
    } else {
      setCurrentExtendedProps(e.event.extendedProps as ExtendedProps);
      setOpenEventInfo(true);
    }
  };

  const changeBookingTime = (
    newBooking: CalendarBookingDomain,
    newStart: Date | null,
    newEnd: Date | null,
    notifyCustomer: boolean
  ) => {
    const payload: UpdateCalendarEventPayload = {
      bookingId: newBooking.id,
      startAt: moment(newStart || newBooking.startAt).format(
        "YYYY-MM-DD-HH:mm:ss"
      ),
      endAt: moment(newEnd || newBooking.endAt).format("YYYY-MM-DD-HH:mm:ss"),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      title: newBooking.formatTitle(),
      description: newBooking.description,
      customerId: newBooking.customerId,
      agentIds: newBooking.agentIds,
      notifyCustomer,
    };
    updateEvent(payload);
    if (errors.length) {
      toast.error(errors[0]);
    } else {
      toast.success("Event updated successfully!");
    }
    setShowNotifyModal(false);
  };

  const useOutsideAlerterRef = useRef<HTMLDivElement>(null);

  const notifyCustomerSubmit = () => {
    setShowNotifyModal(false);
    changeBookingTime(
      changedEventInfo?.event.extendedProps.booking,
      changedEventInfo?.event.start || null,
      changedEventInfo?.event.end || null,
      true
    );
  };

  const dontNotifyCustomerSubmit = (eventInfo?: EventChangeArg) => {
    setShowNotifyModal(false);
    const event = eventInfo?.event || changedEventInfo?.event;
    changeBookingTime(
      event?.extendedProps.booking,
      event?.start || null,
      event?.end || null,
      false
    );
  };

  useEffect(() => {
    if (contactToastMessage.new) {
      if (contactToastMessage.success) {
        toast.success(contactToastMessage.success);
      } else if (contactToastMessage.errors) {
        toast.error(contactToastMessage.errors[0]);
      }
      dispatch(cleanContactsToastMessagesAction());
    }
  }, [contactToastMessage]);

  const hideCalendar =
    isBaseSize && (openEventInfo || openEditForm || openEventForm);

  return (
    <Box
      sx={{
        margin: "15px",
        padding: "10px",
        borderRadius: "15px",
        minHeight: hideCalendar ? "70vh" : "unset",
        ".fc": {
          display: hideCalendar ? "none" : "initial",
        },
        ".chakra-popover__popper": {
          marginTop: "250px",
          marginLeft: "400px",
        },
        ".fc .fc-toolbar-title": {
          fontSize: "sm",
          marginTop: "5px",
          fontWeight: "bold",
        },
        "button.fc-today-button": {
          borderRadius: "63px",
          borderWidth: 0,
          backgroundColor: "var(--chakra-colors-green-500)!important",
          color: "white!important",
          textTransform: "capitalize",
          fontSize: "xs",
          padding: "10px 22px",
        },
        "button.fc-prev-button, button.fc-next-button": {
          background: "none !important",
          border: "none",
          ".fc-icon": {
            color: "black",
          },
        },
        ".fc-dayGridMonth-button, .fc-timeGridWeek-button, .fc-timeGridDay-button, .fc-timeGridFourDay-button":
          {
            borderRadius: "63px",
            borderWidth: 0,
            backgroundColor: "var(--chakra-colors-gray-300)!important",
            color: "white!important",
            textTransform: "capitalize",
            fontSize: "xs",
            padding: "10px 22px",
          },
        ".fc-button-active": {
          color: "white!important",
          backgroundColor: "var(--chakra-colors-blue-400)!important",
        },
        ".fc-toolbar-chunk": {
          display: "flex",
        },
        "button.fc-prev-button .fc-icon, button.fc-next-button .fc-icon": {
          color: "darkgrey",
        },
        "span.fc-scrollgrid-sync-inner": {
          textTransform: "capitalize",
        },
        ".fc-col-header-cell-cushion": {
          fontSize: "sm",
          color: "darkgrey",
          textTransform: "uppercase",
        },
        ".fc .fc-timegrid-slot-minor": {
          border: "none",
        },
        ".fc-timegrid-slot-label-cushion, span.fc-timegrid-axis-cushion": {
          color: "darkgrey",
          fontSize: "sm",
          fontWeight: "bold",
        },
        ".fc-timegrid-event, .fc-daygrid-event": {
          border: "none",
        },
        ".fc-event": {
          fontWeight: "semibold",
        },
        ".fc-highlight": {
          backgroundColor: `${highlightColor.value} !important`,
          boxShadow: "0px 2px 7px rgba(175, 175, 175, 0.5)",
          borderRadius: "4px",
        },
        ".fc-media-screen": {
          height: "100%",
          display: "flex",
        },
        "@media screen and (max-width: 1024px)": {
          ".fc-header-toolbar": {
            flexWrap: "wrap",
            justifyContent: "center",
          },
          ".fc .fc-toolbar-title": {
            fontSize: "xs!important",
          },
          "button.fc-today-button, .fc-dayGridMonth-button, .fc-timeGridWeek-button, .fc-timeGridDay-button, .fc-timeGridFourDay-button":
            {
              padding: "0.4em 0.65em",
            },
          ".fc-col-header-cell-cushion, .fc-timegrid-slot-label-cushion, .fc-daygrid-day-number":
            {
              fontSize: "xs",
            },
          ".fc-daygrid-event": {
            fontSize: "xs",
          },
          ".fc-event-title-container": {
            fontSize: "xs",
          },
          ".chakra-popover__popper": {
            marginTop: "250px",
            marginLeft: "55px",
          },
        },
      }}
      h={{ base: "100%", md: "initial" }}
      display="flex"
      flexDir="column"
    >
      {!(!!isBaseSize && (openEventInfo || openEditForm || openEventForm)) && (
        <Box paddingBottom="15px" width="100%">
          <FormControl>
            <Select
              closeMenuOnSelect={false}
              value={getAgentsByIds(selectedAgentIds).map(
                transformFromAgentDomainToOptionType
              )}
              isMulti
              placeholder="Select Agent"
              onChange={
                handleSelectedAgents as (
                  newValue: unknown,
                  actionMeta: ActionMeta<unknown>
                ) => void
              }
              options={agentOptions}
              styles={getReactSelectStyles(colorMode, colorScheme)}
            />
          </FormControl>
        </Box>
      )}

      <Box ref={useOutsideAlerterRef} h="100%" overflowY="auto">
        <Box h="100%">
          {currentExtendedProps && openEventInfo && (
            <EventInfoPopover
              assignedAgentId={currentExtendedProps?.agentId}
              currentEvent={currentExtendedProps?.booking}
              closeEventPopover={() => setOpenEventInfo(false)}
              onEditClick={() => {
                setOpenEditForm(true);
                setOpenEventInfo(false);
              }}
            />
          )}
          {openEventForm && (
            <NewEventModal
              agents={agents}
              startDate={newEventStartDate}
              endDate={newEventEndDate}
              openEventForm={openEventForm}
              closeNewEventForm={() => setOpenEventForm(false)}
            />
          )}
          {currentExtendedProps && openEditForm && (
            <EditEventModal
              agents={agents}
              closeNewEventForm={() => {
                setOpenEditForm(false);
                setCurrentExtendedProps(undefined);
                setOpenEventInfo(false);
              }}
              currentEvent={currentExtendedProps?.booking}
            />
          )}
          {showNotifyModal && (
            <NotifyGuestModal
              showNotifyModal={showNotifyModal}
              closeNotifyModal={() => setShowNotifyModal(false)}
              onDontNotifyCustomer={dontNotifyCustomerSubmit}
              onNotifyCustomer={notifyCustomerSubmit}
            />
          )}

          <FullCalendar
            headerToolbar={{
              left: isBaseSize ? "prev title next" : "today prev title next",
              right: isBaseSize
                ? "today timeGridDay timeGridFourDay timeGridWeek dayGridMonth"
                : "timeGridDay timeGridFourDay timeGridWeek dayGridMonth",
            }}
            views={{
              dayGridMonth: {
                dayHeaderFormat: { weekday: "short" },
              },
              timeGridFourDay: {
                type: "timeGrid",
                duration: { days: 7 },
                buttonText: "Weekday",
                weekends: false,
                dateAlignment: "week",
                titleFormat: {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
                slotLabelFormat: {
                  hour: "numeric",
                  minute: "2-digit",
                  meridiem: true,
                },
                dayHeaderFormat: { weekday: "short", day: "numeric" },
              },
              timeGridWeek: {
                titleFormat: {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
                slotLabelFormat: {
                  hour: "numeric",
                  minute: "2-digit",
                  meridiem: true,
                },
                dayHeaderFormat: { day: "numeric", weekday: "short" },
              },
              timeGridDay: {
                dayHeaders: false,
                slotLabelFormat: {
                  hour: "numeric",
                  minute: "2-digit",
                  meridiem: true,
                },
              },
            }}
            contentHeight={isBaseSize ? "100%" : 430}
            plugins={[interactionPlugin, timeGridPlugin, daygridPlugin]}
            datesSet={(dateInfo) => {
              fetchEvents({
                startAt: moment(dateInfo.start).format("YYYY-MM-DD-HH:mm:ss"),
                endAt: moment(dateInfo.end).format("YYYY-MM-DD-HH:mm:ss"),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              });
              setHighlightColor(
                dateInfo.view.type === "dayGridMonth"
                  ? Color.WHITE
                  : currentAgent.color
              );
            }}
            initialView={isBaseSize ? "timeGridDay" : "timeGridWeek"}
            nowIndicator={true}
            editable={true}
            select={(e) =>
              !currentExtendedProps
                ? showOpenNewEventForm(e.start, e.end)
                : setCurrentExtendedProps(undefined)
            }
            eventClick={(e) => showEventDetails(e)}
            locale="en"
            selectable={true}
            events={events
              .flatMap(transformToEvents)
              .filter(isBookingEventSelectedByAgent)}
            scrollTime={moment().subtract(1, "hour").format("HH:mm")}
            eventChange={(e) => {
              if (e.oldEvent.extendedProps.booking.notifyChannelSelected) {
                setChangedEventInfo(e);
                setShowNotifyModal(true);
              } else {
                dontNotifyCustomerSubmit(e);
              }
            }}
          />
        </Box>
      </Box>
      {isBaseSize && !openEventForm && !openEditForm && !openEventInfo && (
        <Button
          colorScheme={colorScheme}
          size="sm"
          zIndex={10}
          position="fixed"
          borderRadius="50%"
          width="40px"
          height="40px"
          bottom="60px"
          right="10px"
          onClick={() => setOpenEventForm(true)}
        >
          <Icon
            as={PlusIcon}
            __css={{
              rect: {
                fill: `${colorScheme}.400`,
              },
              path: {
                fill: "white",
              },
            }}
          />
        </Button>
      )}
    </Box>
  );
};

export default FullCalendarComponent;
